import Delay from '../../../../../common/scripts/Toolkit/Utils/Delay';
import Model from '../../../../../common/scripts/Toolkit/Helper/Model';
import DataType from '../Config/DataStructure';
import TableGame from '../../../System/TableGame';
import { PathName } from '../Config/PathConfig';

export enum LobbyJackpotLevelName {
    GRAND = 'grand',
    MEGA = 'mega',
    MAJOR = 'major',
    MAXI = 'maxi',
    MINOR = 'minor',
    MINI = 'mini',
    LINK = 'link',
}

export namespace JackpotLevel {
    const jackpotLevelMap: { [value in DataType.Enums.LobbyJackpotLevel]: LobbyJackpotLevelName } & {
        [name in LobbyJackpotLevelName]: DataType.Enums.LobbyJackpotLevel;
    } = {
        [DataType.Enums.LobbyJackpotLevel.GRAND]: LobbyJackpotLevelName.GRAND,
        [DataType.Enums.LobbyJackpotLevel.MEGA]: LobbyJackpotLevelName.MEGA,
        [DataType.Enums.LobbyJackpotLevel.MAJOR]: LobbyJackpotLevelName.MAJOR,
        [DataType.Enums.LobbyJackpotLevel.MINOR]: LobbyJackpotLevelName.MINOR,
        [DataType.Enums.LobbyJackpotLevel.MINI]: LobbyJackpotLevelName.MINI,
        [DataType.Enums.LobbyJackpotLevel.MAXI]: LobbyJackpotLevelName.MAXI,
        [DataType.Enums.LobbyJackpotLevel.LINK]: LobbyJackpotLevelName.LINK,
        [LobbyJackpotLevelName.GRAND]: DataType.Enums.LobbyJackpotLevel.GRAND,
        [LobbyJackpotLevelName.MEGA]: DataType.Enums.LobbyJackpotLevel.MEGA,
        [LobbyJackpotLevelName.MAJOR]: DataType.Enums.LobbyJackpotLevel.MAJOR,
        [LobbyJackpotLevelName.MINOR]: DataType.Enums.LobbyJackpotLevel.MINOR,
        [LobbyJackpotLevelName.MINI]: DataType.Enums.LobbyJackpotLevel.MINI,
        [LobbyJackpotLevelName.MAXI]: DataType.Enums.LobbyJackpotLevel.MAXI,
        [LobbyJackpotLevelName.LINK]: DataType.Enums.LobbyJackpotLevel.LINK,
    };
    export function map(name: LobbyJackpotLevelName): DataType.Enums.LobbyJackpotLevel;
    export function map(type: DataType.Enums.LobbyJackpotLevel): LobbyJackpotLevelName;
    export function map(arg: DataType.Enums.LobbyJackpotLevel | LobbyJackpotLevelName): LobbyJackpotLevelName | DataType.Enums.LobbyJackpotLevel {
        return jackpotLevelMap[arg];
    }
    Object.defineProperty(JackpotLevel, 'map', {
        enumerable: false,
    });
    /**
     * return levels in descending order
     */
    export function list() {
        return [
            DataType.Enums.LobbyJackpotLevel.GRAND,
            DataType.Enums.LobbyJackpotLevel.MEGA,
            DataType.Enums.LobbyJackpotLevel.MAJOR,
            DataType.Enums.LobbyJackpotLevel.MAXI,
            DataType.Enums.LobbyJackpotLevel.MINOR,
            DataType.Enums.LobbyJackpotLevel.MINI,
            DataType.Enums.LobbyJackpotLevel.LINK,
        ];
    }
    Object.defineProperty(JackpotLevel, 'list', {
        enumerable: false,
    });
}

enum EventType {
    JACKPOT_STATUS_UPDATED = 'JACKPOT_STATUS_UPDATED',
    JACKPOT_WIN_RECEIVED = 'JACKPOT_WIN_RECEIVED',
    JACKPOT_RECORD_RECEIVED = 'JACKPOT_RECORD_RECEIVED',
}

export default abstract class LobbyJackpotModel extends Model {
    public static readonly EVENT_TYPE = EventType;

    public readonly poolMap: { [jackpotPoolType: string]: { [value in DataType.Enums.LobbyJackpotLevel]?: DataType.Jackpot.PoolData } } = {};
    protected readonly _winResults: DataType.Jackpot.WinResultData[] = [];
    protected readonly _winRecords: DataType.Jackpot.WinRecordData[] = [];
    protected _applyJackpot: boolean = true;

    constructor() {
        super();
    }

    public get isJackpotApplied(): boolean {
        return this._applyJackpot;
    }

    public get isOpen(): boolean {
        return Object.values(this.poolList).length > 0;
    }

    public get poolList(): { [jackpotPoolType: string]: DataType.Jackpot.PoolData[] } {
        return Object.fromEntries(Object.entries(this.poolMap).map(([key, value]) => [key, Object.values(value)]));
    }

    public init() {
        TableGame.connection.on(PathName.Login, this.onLoginDataReceived, this);
        TableGame.connection.on(PathName.JackpotStatus, this.onJackpotStatusReceived, this);
        TableGame.connection.on(PathName.Bet, this.handleResult, this);
    }

    public requestJackpotStatus(): Promise<void> {
        if (!this.isJackpotApplied) {
            return Promise.resolve();
        }
        return TableGame.connection.send(PathName.JackpotStatus);
    }

    public getWinResults(): DataType.Jackpot.WinResultData[] {
        const results = [...this._winResults];
        this._winResults.length = 0;
        return results;
    }

    public getWinRecords(): Readonly<DataType.Jackpot.WinRecordData[]> {
        return this._winRecords;
    }

    public async syncLatestWinRecords(): Promise<void> {
        if (!TableGame.jackpot.isJackpotApplied) {
            return;
        }
        // FIX
        // data is sorted from newest to oldest
        const records = await TableGame.connection.send(PathName.JackpotRecord);
        this.handleWinRecord(records.Results);
    }

    protected async handleResult(result: DataType.Bet.ResponseData) {
        const { Jackpot } = result;
        if (Jackpot) {
            this._winResults.length = 0;
            this._winResults.push(...Jackpot);
            await Delay.resolve(500);
            this.emit(EventType.JACKPOT_WIN_RECEIVED);
        }
    }

    protected abstract onLoginDataReceived(data: DataType.User.ResponseData): void;
    protected abstract onJackpotStatusReceived(data: DataType.Jackpot.StatusResponseData): void;
    protected abstract handleWinRecord(data: DataType.Jackpot.WinRecordData[]): void;
}
